import { Button, DatePicker, Form, message, Space } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiDownload } from "react-icons/fi";
import { useSelector } from "react-redux";

import { exportInsights } from "../actions/acc_actions";
import ActiveUserChart from "./insights/ActiveUserChart";
import RewardsByOutletChart from "./insights/RewardsByOutletChart";
import RewardsChart from "./insights/RewardsChart";
import StampsChart from "./insights/StampsChart";

export default function InsightsForm({ selectedSchema }) {
  const { t } = useTranslation();
  const businessSlug = useSelector((state) => state.account.businessSlug);

  const [form] = Form.useForm();
  const [fromDate, setFromDate] = useState(
    dayjs().subtract(1, "month").format("YYYY-MM-DD"),
  );
  const [toDate, setToDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [exportLoading, setExportLoading] = useState(false);

  const onSubmit = () => {
    const values = form.getFieldsValue();
    const { fromDate: formFromDate, toDate: formToDate } = values;

    if (!formFromDate || !formToDate) {
      message.error(t("select_both_dates"));
      return;
    }

    setFromDate(formFromDate.format("YYYY-MM-DD"));
    setToDate(formToDate.format("YYYY-MM-DD"));
  };

  const onExport = async () => {
    try {
      setExportLoading(true);

      const values = form.getFieldsValue();
      const { fromDate: formFromDate, toDate: formToDate } = values;

      const exportFrom = formFromDate
        ? formFromDate.format("YYYY-MM-DD")
        : fromDate;
      const exportTo = formToDate ? formToDate.format("YYYY-MM-DD") : toDate;

      const params = {
        loyalty_scheme_slug: selectedSchema,
        from: exportFrom,
        to: exportTo,
        refresh: true,
      };

      const result = await exportInsights(params);

      if (result.success) {
        message.success(t("export_success"));
      } else {
        message.error(t("export_failed"));
      }
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="insights-form">
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          fromDate: dayjs().subtract(1, "month"),
          toDate: dayjs(),
        }}
      >
        <div className="form-row">
          <Form.Item
            className="date-picker"
            name="fromDate"
            label={t("from_date")}
            rules={[{ required: true, message: t("select_from_date") }]}
          >
            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            className="date-picker"
            name="toDate"
            label={t("to_date")}
            rules={[{ required: true, message: t("select_to_date") }]}
          >
            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item className="insights-form-btns">
            <Space>
              <Button
                style={{ width: "16rem" }}
                type="primary"
                variant="solid"
                onClick={onSubmit}
              >
                {t("submit")}
              </Button>
              <Button
                onClick={() => {
                  form.resetFields();
                  setFromDate(
                    dayjs().subtract(1, "month").format("YYYY-MM-DD"),
                  );
                  setToDate(dayjs().format("YYYY-MM-DD"));
                }}
              >
                {t("reset")}
              </Button>
              <Button
                style={{ width: "16rem" }}
                type="primary"
                variant="solid"
                icon={<FiDownload />}
                loading={exportLoading}
                onClick={onExport}
              >
                {t("export")}
              </Button>
            </Space>
          </Form.Item>
        </div>
      </Form>

      <ActiveUserChart
        selectedSchema={selectedSchema}
        dateFrom={fromDate}
        dateTo={toDate}
      />
      <StampsChart
        selectedSchema={selectedSchema}
        dateFrom={fromDate}
        dateTo={toDate}
      />
      <RewardsChart
        selectedSchema={selectedSchema}
        dateFrom={fromDate}
        dateTo={toDate}
      />
      <RewardsByOutletChart
        selectedSchema={selectedSchema}
        dateFrom={fromDate}
        dateTo={toDate}
      />
    </div>
  );
}
